import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { PageContainer, StyledCard } from '../components/ui/StyledComponents'
import supabase from '../supabaseClient'
import { useAuth } from '../context/AuthContext'

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: { xs: 1.5, sm: 2 } }}>{children}</Box>}
    </div>
  )
}

function ArchivePage() {
  const { userRole } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [tab, setTab] = useState(0)
  const [inactiveGuardians, setInactiveGuardians] = useState([])
  const [inactiveTeachers, setInactiveTeachers] = useState([])
  const [inactiveStudents, setInactiveStudents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const isAdmin = userRole === 'admin' || userRole === 'super_admin'

  useEffect(() => {
    if (isAdmin) {
      fetchAllInactive()
    }
  }, [isAdmin, tab])

  const fetchAllInactive = async () => {
    try {
      setLoading(true)
      setError('')

      const [guardiansResult, teachersResult, studentsResult] = await Promise.all([
        supabase
          .from('guardians')
          .select('id, first_name, middle_name, last_name, email, phone, gender, is_active, created_at, archive_reason')
          .eq('is_active', false)
          .order('last_name'),
        supabase
          .from('teachers')
          .select('id, first_name, last_name, email, phone, gender, is_active, created_at, archive_reason')
          .eq('is_active', false)
          .order('last_name'),
        supabase
          .from('students')
          .select('id, first_name, last_name, date_of_birth, gender, is_active, created_at, archive_reason')
          .eq('is_active', false)
          .order('last_name'),
      ])

      if (guardiansResult.error) throw guardiansResult.error
      if (teachersResult.error) throw teachersResult.error
      if (studentsResult.error) throw studentsResult.error

      setInactiveGuardians(guardiansResult.data || [])
      setInactiveTeachers(teachersResult.data || [])
      setInactiveStudents(studentsResult.data || [])
    } catch (error) {
      console.error('Error fetching archived users:', error)
      setError('Failed to load archived users')
    } finally {
      setLoading(false)
    }
  }

  const handleGuardianClick = (guardianId) => {
    navigate(`/parent_details/${guardianId}`)
  }

  const handleTeacherClick = (teacherId) => {
    navigate(`/teacher_details/${teacherId}`)
  }

  const handleStudentClick = (studentId) => {
    navigate(`/student_details/${studentId}`)
  }

  // Check if search term is numeric (contains digits)
  const isNumericSearch = (term) => {
    return /\d/.test(term)
  }

  // Filter guardians based on search term
  const filterGuardians = (guardians) => {
    if (!searchTerm.trim()) {
      return guardians
    }

    const searchLower = searchTerm.toLowerCase().trim()
    const isNumeric = isNumericSearch(searchTerm)

    return guardians.filter((guardian) => {
      if (isNumeric) {
        const phone = guardian.phone || ''
        const phoneDigits = phone.replace(/\D/g, '')
        const searchDigits = searchTerm.replace(/\D/g, '')
        return phoneDigits.includes(searchDigits)
      } else {
        const fullName = `${guardian.first_name || ''} ${guardian.middle_name || ''} ${guardian.last_name || ''}`.toLowerCase()
        return fullName.includes(searchLower)
      }
    })
  }

  // Filter teachers based on search term
  const filterTeachers = (teachers) => {
    if (!searchTerm.trim()) {
      return teachers
    }

    const searchLower = searchTerm.toLowerCase().trim()
    const isNumeric = isNumericSearch(searchTerm)

    return teachers.filter((teacher) => {
      if (isNumeric) {
        const phone = teacher.phone || ''
        const phoneDigits = phone.replace(/\D/g, '')
        const searchDigits = searchTerm.replace(/\D/g, '')
        return phoneDigits.includes(searchDigits)
      } else {
        const fullName = `${teacher.first_name || ''} ${teacher.last_name || ''}`.toLowerCase()
        return fullName.includes(searchLower)
      }
    })
  }

  // Filter students based on search term
  const filterStudents = (students) => {
    if (!searchTerm.trim()) {
      return students
    }

    const searchLower = searchTerm.toLowerCase().trim()

    return students.filter((student) => {
      const fullName = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase()
      return fullName.includes(searchLower)
    })
  }

  const filteredInactiveGuardians = filterGuardians(inactiveGuardians)
  const filteredInactiveTeachers = filterTeachers(inactiveTeachers)
  const filteredInactiveStudents = filterStudents(inactiveStudents)

  // Combine all users for "All" tab
  const getAllInactiveUsers = () => {
    const guardians = filteredInactiveGuardians.map(g => ({ ...g, userType: 'Guardian', typeId: g.id }))
    const teachers = filteredInactiveTeachers.map(t => ({ ...t, userType: 'Teacher', typeId: t.id }))
    const students = filteredInactiveStudents.map(s => ({ ...s, userType: 'Student', typeId: s.id }))
    return [...guardians, ...teachers, ...students]
  }

  const allInactiveUsers = getAllInactiveUsers()
  const totalAllInactive = inactiveGuardians.length + inactiveTeachers.length + inactiveStudents.length

  if (!isAdmin) {
    return (
      <PageContainer>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' },
          mb: { xs: 2, sm: 3 },
        }}
      >
        Archive
      </Typography>

      <StyledCard>
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <TextField
            fullWidth
            placeholder="Search by Name or Phone Number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              maxWidth: { xs: '100%', sm: 400 },
              '& .MuiInputBase-input': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </Box>
        <Tabs 
          value={tab} 
          onChange={(e, newValue) => setTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile={isMobile}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              minWidth: { xs: 'auto', sm: 0 },
              px: { xs: 1, sm: 2 },
              py: { xs: 1, sm: 1.5 },
            },
          }}
        >
          <Tab label={`All (${allInactiveUsers.length}${searchTerm ? ` / ${totalAllInactive}` : ''})`} />
          <Tab label={`Guardians (${filteredInactiveGuardians.length}${searchTerm ? ` / ${inactiveGuardians.length}` : ''})`} />
          <Tab label={`Teachers (${filteredInactiveTeachers.length}${searchTerm ? ` / ${inactiveTeachers.length}` : ''})`} />
          <Tab label={`Students (${filteredInactiveStudents.length}${searchTerm ? ` / ${inactiveStudents.length}` : ''})`} />
        </Tabs>

        <TabPanel value={tab} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {error}
            </Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Phone
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Archive Reason
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allInactiveUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 7} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {searchTerm ? 'No archived users match your search.' : 'No archived users found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    allInactiveUsers.map((user) => (
                      <TableRow
                        key={`${user.userType}-${user.typeId}`}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (user.userType === 'Guardian') {
                            handleGuardianClick(user.typeId)
                          } else if (user.userType === 'Teacher') {
                            handleTeacherClick(user.typeId)
                          } else {
                            handleStudentClick(user.typeId)
                          }
                        }}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip 
                            label={user.userType} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {user.userType === 'Guardian' 
                                ? `${user.first_name} ${user.middle_name ? `${user.middle_name} ` : ''}${user.last_name}`
                                : `${user.first_name} ${user.last_name}`
                              }
                            </Typography>
                            {/* Show email on mobile */}
                            {isMobile && user.email && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {user.email}
                              </Typography>
                            )}
                            {/* Show phone on mobile */}
                            {isMobile && user.phone && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {user.phone}
                              </Typography>
                            )}
                            {/* Show gender on mobile */}
                            {isMobile && user.gender && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {user.gender}
                              </Typography>
                            )}
                            {/* Show archive reason on mobile */}
                            {isMobile && user.archive_reason && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                Reason: {user.archive_reason}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', md: 'table-cell' },
                        }}>
                          {user.email || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {user.phone || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', sm: 'table-cell' },
                        }}>
                          {user.gender || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip 
                            label="Archived" 
                            color="default" 
                            size="small"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {user.archive_reason ? (
                            <Tooltip title={user.archive_reason} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { xs: 150, sm: 200 },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                                {user.archive_reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {error}
            </Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Phone
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Archive Reason
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInactiveGuardians.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 2 : 6} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {searchTerm ? 'No archived guardians match your search.' : 'No archived guardians found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInactiveGuardians.map((guardian) => (
                      <TableRow
                        key={guardian.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleGuardianClick(guardian.id)}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {guardian.first_name} {guardian.middle_name ? `${guardian.middle_name} ` : ''}{guardian.last_name}
                            </Typography>
                            {/* Show email on mobile */}
                            {isMobile && guardian.email && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {guardian.email}
                              </Typography>
                            )}
                            {/* Show phone on mobile */}
                            {isMobile && guardian.phone && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {guardian.phone}
                              </Typography>
                            )}
                            {/* Show gender on mobile */}
                            {isMobile && guardian.gender && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {guardian.gender}
                              </Typography>
                            )}
                            {/* Show archive reason on mobile */}
                            {isMobile && guardian.archive_reason && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                Reason: {guardian.archive_reason}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', md: 'table-cell' },
                        }}>
                          {guardian.email || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {guardian.phone || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', sm: 'table-cell' },
                        }}>
                          {guardian.gender || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip 
                            label="Archived" 
                            color="default" 
                            size="small"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {guardian.archive_reason ? (
                            <Tooltip title={guardian.archive_reason} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { xs: 150, sm: 200 },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                                {guardian.archive_reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tab} index={2}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {error}
            </Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', md: 'table-cell' },
                    }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Phone
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Archive Reason
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInactiveTeachers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 2 : 6} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {searchTerm ? 'No archived teachers match your search.' : 'No archived teachers found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInactiveTeachers.map((teacher) => (
                      <TableRow
                        key={teacher.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTeacherClick(teacher.id)}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {teacher.first_name} {teacher.last_name}
                            </Typography>
                            {/* Show email on mobile */}
                            {isMobile && teacher.email && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {teacher.email}
                              </Typography>
                            )}
                            {/* Show phone on mobile */}
                            {isMobile && teacher.phone && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {teacher.phone}
                              </Typography>
                            )}
                            {/* Show gender on mobile */}
                            {isMobile && teacher.gender && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {teacher.gender}
                              </Typography>
                            )}
                            {/* Show archive reason on mobile */}
                            {isMobile && teacher.archive_reason && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                Reason: {teacher.archive_reason}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', md: 'table-cell' },
                        }}>
                          {teacher.email || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {teacher.phone || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', sm: 'table-cell' },
                        }}>
                          {teacher.gender || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip 
                            label="Archived" 
                            color="default" 
                            size="small"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {teacher.archive_reason ? (
                            <Tooltip title={teacher.archive_reason} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { xs: 150, sm: 200 },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                                {teacher.archive_reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tab} index={3}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: { xs: 2, sm: 3 } }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {error}
            </Alert>
          ) : (
            <TableContainer 
              component={Paper} 
              variant="outlined"
              sx={{
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      Date of Birth
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}>
                      Gender
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                    }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}>
                      Archive Reason
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInactiveStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 2 : 5} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {searchTerm ? 'No archived students match your search.' : 'No archived students found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInactiveStudents.map((student) => (
                      <TableRow
                        key={student.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleStudentClick(student.id)}
                      >
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                              {student.first_name} {student.last_name}
                            </Typography>
                            {/* Show date of birth on mobile */}
                            {isMobile && student.date_of_birth && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                DOB: {student.date_of_birth}
                              </Typography>
                            )}
                            {/* Show gender on mobile */}
                            {isMobile && student.gender && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                {student.gender}
                              </Typography>
                            )}
                            {/* Show archive reason on mobile */}
                            {isMobile && student.archive_reason && (
                              <Typography 
                                variant="caption" 
                                color="textSecondary"
                                sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}
                              >
                                Reason: {student.archive_reason}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', sm: 'table-cell' },
                        }}>
                          {student.date_of_birth || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', sm: 'table-cell' },
                        }}>
                          {student.gender || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          <Chip 
                            label="Archived" 
                            color="default" 
                            size="small"
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: '20px', sm: '24px' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          display: { xs: 'none', lg: 'table-cell' },
                        }}>
                          {student.archive_reason ? (
                            <Tooltip title={student.archive_reason} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { xs: 150, sm: 200 },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: { xs: '0.875rem', sm: '1rem' },
                                }}
                              >
                                {student.archive_reason}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </StyledCard>
    </PageContainer>
  )
}

export default ArchivePage

